/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useState, useCallback } from 'preact/hooks';
import { useTooltipControlled } from './useTooltipControlled';
import { TestIdProps } from '../UNSAFE_useTestId';

const defaultOffset = 8; // Redwood tooltip has a default offset of 8 pixels

type tooltipParameters = Parameters<typeof useTooltipControlled>[0];

type Props = {
  text?: string;
  position?: tooltipParameters['position'];
  isDisabled?: boolean;
  anchor?: tooltipParameters['anchor'];
  offset?: { mainAxis?: number; crossAxis?: number };
  variant?: tooltipParameters['variant'];
} & TestIdProps;

/**
 * A hook with tooltip implementation.
 * Returns event handlers that can enhance the target element with a tooltip displayed
 * on hover and focus.
 *
 * @param text tooltip text
 * @param position tooltip placement relative to the target element
 * @param isDisabled determines if the tooltip is disabled
 * @param anchor determines if the target is an element or the pointer
 * @param offset offset from the placement
 * @param variant specifies if the rendered popup should have 'tooltip' or 'datatip' look and behavior
 * @returns an object with the 'tooltipContent' and 'tooltipProps' properties.
 */
export const useTooltip = ({
  text,
  position = 'bottom',
  isDisabled = false,
  anchor = { x: 'element', y: 'element' },
  offset,
  variant = 'tooltip',
  testId
}: Props): ReturnType<typeof useTooltipControlled> => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const disabled = isDisabled || !text;
  const newOffset = offset ? offset : { mainAxis: defaultOffset, crossAxis: 0 };

  const onToggleHandler = useCallback((details: { value: boolean }) => {
    setIsOpen(details.value);
  }, []);

  const { tooltipContent, tooltipProps } = useTooltipControlled({
    text,
    isOpen,
    position,
    isDisabled: disabled,
    offset: newOffset,
    anchor,
    variant,
    onToggle: onToggleHandler,
    testId
  });

  return {
    tooltipContent,
    tooltipProps
  };
};
